import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2, CheckCircle, AlertCircle, HelpCircle } from 'lucide-react';
import { submitMemberSupport } from '../../services/api';

const MemberSupportPage = () => {
  const { user } = useAuth();
  
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    
    setIsSubmitting(true);
    setError(null);

    try {
      await submitMemberSupport({ message });
      setSubmitSuccess(true);
      setMessage('');
    } catch (err) {
      setError('Failed to send your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 md:p-12" data-testid="member-support-page">
      <div className="max-w-2xl">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          Member Support
        </h1>
        <p className="text-gray-600 mb-8">
          Contact the lodge administration with questions, concerns, or requests.
        </p>

        <Card className="border-gray-100" data-testid="support-form-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-emerald-600" />
              Submit a Support Request
            </CardTitle>
            <CardDescription>
              Your message will be sent directly to the lodge administration.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitSuccess ? (
              <div className="text-center py-8" data-testid="support-success">
                <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">
                  Message Sent Successfully
                </h3>
                <p className="text-gray-600 mb-4">
                  Thank you for contacting us. We will respond to your enquiry shortly.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setSubmitSuccess(false)}
                  data-testid="send-another-btn"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" data-testid="support-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* Pre-populated member info (locked) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Your Name</Label>
                    <Input
                      id="name"
                      value={`${user?.firstName} ${user?.lastName}`}
                      disabled
                      className="bg-gray-100"
                      data-testid="support-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Your Email</Label>
                    <Input
                      id="email"
                      value={user?.email}
                      disabled
                      className="bg-gray-100"
                      data-testid="support-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Please describe your question, concern, or request..."
                    rows={6}
                    required
                    data-testid="support-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={isSubmitting}
                  data-testid="support-submit-btn"
                >
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send Message
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Additional Help */}
        <Card className="border-gray-100 mt-6 bg-gradient-to-br from-gray-50 to-white">
          <CardContent className="p-6">
            <h3 className="font-heading font-semibold text-gray-900 mb-2">Other Ways to Get Help</h3>
            <ul className="text-gray-600 space-y-2 text-sm">
              <li>• For meeting-related queries, you can also submit comments on specific meeting minutes.</li>
              <li>• For dues and payment issues, please use WebCollect or contact the Treasurer directly.</li>
              <li>• For urgent matters, you may speak with an office-bearer at the next meeting.</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MemberSupportPage;
